/**
 * Attendu 620 — Relier une fraction au résultat exact de la division
 * de son numérateur par son dénominateur.
 *
 * 10 générateurs couvrant : écriture fractionnaire d'un quotient,
 * valeur décimale, égalités à trou, QCM / saisie / relier / V-F / texte-trou.
 */

import {
    randInt,
    randomChoice,
    shuffle,
    makeQcm,
    makeSaisie,
    makeVraiFaux,
    makeRelier,
    makeTexteTrou,
} from '../../../utils';

import {
    mkFrac,
    gcd,
    toDecimalString,
    renderFrac,
    fmtItemBrut,
} from './fraction.js';

const ATTENDU = 620;

const exprElement = (katexStr) => ({
    version: 1,
    elements: [{ type: 'expression', config: { contenu: katexStr } }],
});

// Dénominateurs des questions « valeur décimale » — calibrage 6e SANS
// calculatrice (revue 2026-08-01, aligné sur la frontière du programme :
// division posée exigible = diviseur à 1 chiffre) : 8 retiré (3 décimales),
// 25 et 50 retirés (diviseur 2 chiffres + astuce « ×4 ÷100 » hors réflexes
// 6e) ; 20 gardé (astuce naturelle ÷10 puis ÷2). Les tirages sont de plus
// gardés sur le dénominateur SIMPLIFIÉ (44/100 s'affiche 11/25 sinon).
const DEC_DENS = [2, 4, 5, 10, 20, 100];

// Pool restreint pour les exemples de flash / relier
const FLASH_POOL = [
    { num: 3, den: 5 }, { num: 1, den: 10 }, { num: 8, den: 5 }, { num: 3, den: 2 }, { num: 13, den: 10 },
    { num: 7, den: 4 }, { num: 9, den: 4 }, { num: 11, den: 5 }, { num: 7, den: 20 }, { num: 17, den: 10 },
    { num: 1, den: 2 }, { num: 1, den: 4 }, { num: 5, den: 4 }, { num: 3, den: 4 }, { num: 1, den: 5 },
];

/* ──────────────────────────────────────────────────────────────
 * 620-T1 — QCM : écriture fractionnaire d'un quotient verbalisé
 * ────────────────────────────────────────────────────────────── */
export const genFractionQuotientQcm = () => {
    // a, b premiers entre eux (revue 2026-08-01) : la division ne tombe
    // jamais juste, la fraction EST le résultat exact — et l'affichage est
    // BRUT (fmtItemBrut) : « quotient de 22 par 2 » affichait la bonne
    // réponse simplifiée « 11 » (38 % des tirages, dont 12 % en entier).
    // L'ancienne garde avait deux conditions mortes (a+b===a, quotients
    // entiers consécutifs égaux), purgées.
    let a, b;
    do {
        a = randInt(2, 60);
        b = randInt(2, 20);
    } while (gcd(a, b) !== 1);
    const correct = fmtItemBrut(a, b);
    const distractors = [
        fmtItemBrut(b, a),
        fmtItemBrut(a + b, b),
        `$${a} \\times ${b}$`,
    ];
    const texte = `<p>Quelle est l'écriture fractionnaire du quotient de <strong>${a}</strong> par <strong>${b}</strong> ?</p>`;
    return makeQcm([correct, ...distractors], correct, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Écrire un quotient verbalisé sous forme fractionnaire",
    });
};

/* ──────────────────────────────────────────────────────────────
 * 620-T2 — QCM : valeur décimale d'une fraction simple
 * ────────────────────────────────────────────────────────────── */
export const genValeurDecimaleQcm = () => {
    // Pool refondu (revue 2026-08-01) : TOUT est construit sur la fraction
    // AFFICHÉE (f.num/f.den, simplifiée) — l'ancien moteur fabriquait
    // « 34,20 » et « 20/34 » sur les valeurs d'ORIGINE d'un tirage 34/20
    // affiché 17/10, des nombres invisibles pour l'élève. Retirage complet
    // si 4 options distinctes sont impossibles (jamais d'option inventée).
    for (let t = 0; t < 30; t++) {
        let f;
        do {
            const d = randomChoice(DEC_DENS);
            const a = randInt(1, Math.min(60, d * 15));
            f = mkFrac(a, d);
        } while (!DEC_DENS.includes(f.den));
        const val = toDecimalString(f);
        // collage : « la barre de fraction, c'est une virgule »
        const collage = `${f.num},${f.den}`;
        // inversion : division dans le mauvais sens — en décimal si ça
        // existe, sinon la fraction inverse en vrai dfrac (arbitrage
        // utilisateur : l'élève qui inverse sans calculer la choisit)
        const invDec = toDecimalString(mkFrac(f.den, f.num));
        const inversion = invDec !== null ? invDec : `$\\dfrac{${f.den}}{${f.num}}$`;
        // troncature : division arrêtée avant la virgule
        const trunc = String(Math.trunc(f.num / f.den));
        const distracteurs = new Set([collage, inversion, trunc]);
        distracteurs.delete(val);
        // filet : bruit EN VALEUR, sans zéro traînant
        let garde = 0;
        while (distracteurs.size < 3 && garde < 10) {
            garde += 1;
            const bruit = String(Math.round((f.num / f.den + randInt(1, 3) / 10) * 100) / 100).replace('.', ',');
            if (bruit !== val) distracteurs.add(bruit);
        }
        const choices = [val, ...[...distracteurs].slice(0, 3)];
        if (new Set(choices).size !== 4) continue;

        const texte = `<p>Quelle est la valeur décimale de {{element:0}} ?</p>`;
        return makeQcm(choices, val, texte, {
            niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
            label: "Calculer la valeur décimale d'une fraction",
            elements_additionnels: exprElement(renderFrac(f)),
        });
    }
    throw new Error('gen_620 : 30 retirages sans 4 options distinctes — moteur à auditer');
};

/* ──────────────────────────────────────────────────────────────
 * 620-T3 — Saisie : écriture fractionnaire d'un quotient
 * ────────────────────────────────────────────────────────────── */
export const genFractionQuotientTexte = () => {
    // a, b premiers entre eux (revue 2026-08-01) : une seule écriture exacte
    // possible — l'ancienne saisie texte « a/b » comptait FAUSSE la fraction
    // simplifiée pourtant juste (41 % des tirages exposés, « 6 » rejeté pour
    // « 30 par 5 »). Fraction verticale à champs (motif 652) : le geste est
    // la transcription positionnelle — dividende en haut, diviseur en bas.
    let a, b;
    do {
        a = randInt(2, 60);
        b = randInt(2, 30);
    } while (gcd(a, b) !== 1);
    const texte = `<p>Écris, sous forme d'une fraction, le résultat exact de la division de <strong>${a}</strong> par <strong>${b}</strong>.</p>`;
    const phrase = `$\\text{Réponse : } \\dfrac{{{${a}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}$`;
    return makeTexteTrou(phrase, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Écrire un quotient verbalisé sous forme fractionnaire",
        mode: 'saisie',
        strict: false,
    });
};

/* ──────────────────────────────────────────────────────────────
 * 620-T4 — Saisie : compléter a = ?/b (numérateur manquant)
 * a est un entier OU décimal simple, b est sympathique
 * ────────────────────────────────────────────────────────────── */
export const genEgaliteNumTexte = () => {
    // On choisit b, puis k entier, puis on pose a = k/b (décimal ou entier)
    const b = randomChoice([2, 4, 5, 10, 20]);
    const num = randInt(1, 40); // on veut que a = num/b soit "propre"
    const aStr = toDecimalString(mkFrac(num, b));
    const texte = `<p>Complète pour que l'égalité soit vraie :</p><p><strong>{{element:0}}</strong></p>`;
    return makeSaisie(String(num), texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Compléter une égalité a = ?/b",
        strict: true,
        elements_additionnels: exprElement(`${aStr} = \\dfrac{\\;?\\;}{${b}}`),
    });
};

/* ──────────────────────────────────────────────────────────────
 * 620-T5 — Saisie : compléter a/? = k ou ?/b = k
 * ────────────────────────────────────────────────────────────── */
export const genEgaliteTrouTexte = () => {
    // 100 % DÉNOMINATEUR (arbitrage 2026-08-01) : l'étiquette de la carte
    // (« Saisie (dén) ») dit enfin vrai — le numérateur est déjà couvert par
    // les deux autres formats. Pool enrichi à 45 variantes (5 diviseurs ×
    // 9 quotients), toutes « petite table × décimal ami » : entrée de gamme
    // en quotients entiers, sommet en 0,75 (2 cas sur 45).
    // On construit à l'envers : choisit ? = b et d, pose a = b·d.
    const b = randomChoice([2, 3, 4, 5, 10]);
    const d = randomChoice([2, 3, 0.5, 1.5, 2.5, 0.25, 0.75, 0.2, 0.1]);
    const a = Math.round(b * d * 100) / 100;
    // Formatage FR
    const dStr = d.toString().replace('.', ',');
    const aStr = (Number.isInteger(a) ? a.toString() : a.toString().replace('.', ','));
    const texte = `<p>Complète pour que l'égalité soit vraie :</p><p><strong>{{element:0}}</strong></p>`;
    return makeSaisie(String(b), texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Compléter une égalité à trou a/? = décimal",
        strict: true,
        elements_additionnels: exprElement(`\\dfrac{${aStr}}{\\;?\\;} = ${dStr}`),
    });
};

/* ──────────────────────────────────────────────────────────────
 * 620-T6 — Saisie : valeur décimale d'une fraction
 * ────────────────────────────────────────────────────────────── */
export const genValeurDecimaleTexte = () => {
    let f;
    do {
        const d = randomChoice(DEC_DENS);
        // Plafond aligné sur le QCM (revue 2026-08-01) : l'ancien min(250, d×40)
        // donnait des 199/5 — de l'endurance calculatoire, pas l'attendu.
        const a = randInt(1, Math.min(60, d * 15));
        f = mkFrac(a, d);
    } while (!DEC_DENS.includes(f.den));
    const val = toDecimalString(f);
    const texte = `<p>Donne la valeur décimale de {{element:0}}.</p>`;
    return makeSaisie(val, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Calculer la valeur décimale d'une fraction",
        strict: true,
        elements_additionnels: exprElement(renderFrac(f)),
    });
};

/* ──────────────────────────────────────────────────────────────
 * 620-T7 — Relier fraction ↔ écriture décimale
 * ────────────────────────────────────────────────────────────── */
export const genValeurDecimaleRelier = () => {
    // Refonte anti-déduction (revue 2026-08-01, gabarit arbitré : 3 sources /
    // 6 cibles) : l'ancien 5 ↔ 5 bijectif offrait la dernière paire, et la
    // seule fraction ≥ 2 se repérait par sa cible sans division. Chaque
    // fraction tirée amène son LEURRE « collage » (3/4 → « 3,4 », le piège
    // que la fiche enseigne). Garde numérique par RETIRAGE : le collage de
    // 1/5 vaut « 1,5 »... la valeur exacte de 3/2 — les 6 valeurs doivent
    // être deux à deux distinctes.
    const POOL_RELIER = FLASH_POOL.filter(f => f.den <= 5);
    const collageTxt = (f) => `${f.num},${f.den}`;

    for (let t = 0; t < 60; t++) {
        const trois = shuffle([...POOL_RELIER]).slice(0, 3);
        const valeurs = [
            ...trois.map(f => f.num / f.den),
            ...trois.map(f => f.num + f.den / 10),
        ];
        if (new Set(valeurs.map(v => v.toFixed(4))).size !== 6) continue;

        const pairs = trois.map(f => ({
            source: `$${renderFrac(f)}$`,
            target: toDecimalString(f),
        }));
        const leurres = trois.map(f => ({
            target: collageTxt(f),
            code: 'collage_barre_virgule',
        }));
        const texte = '<p>Relie chaque fraction à son écriture décimale. '
            + 'Attention : certaines écritures ne correspondent à aucune fraction.</p>';
        const payload = makeRelier(pairs, texte, {
            niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
            label: "Flash — valeur décimale d'une fraction simple",
            cibles_leurres: leurres,
        });
        payload._meta.params = {
            fractions: trois.map(f => `${f.num}/${f.den}`),
            distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
        };
        return payload;
    }
    // Repli déterministe vérifié : 1/2, 3/4, 9/4 — valeurs 0,5 / 0,75 / 2,25
    // et collages 1,2 / 3,4 / 9,4 : six valeurs distinctes.
    const repli = [{ num: 1, den: 2 }, { num: 3, den: 4 }, { num: 9, den: 4 }];
    return makeRelier(
        repli.map(f => ({ source: `$${renderFrac(f)}$`, target: toDecimalString(f) })),
        '<p>Relie chaque fraction à son écriture décimale. '
        + 'Attention : certaines écritures ne correspondent à aucune fraction.</p>',
        {
            niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
            label: "Flash — valeur décimale d'une fraction simple",
            cibles_leurres: repli.map(f => ({ target: collageTxt(f), code: 'collage_barre_virgule' })),
        });
};

/* ──────────────────────────────────────────────────────────────
 * 620-T8 — Tableau V/F : égalités fraction = décimal
 * ────────────────────────────────────────────────────────────── */
export const genValeurDecimaleVf = () => {
    // Construit 4 lignes avec un mix V/F équilibré
    const lignes = [];
    const bonnes = [];
    const wanted = shuffle([0, 0, 1, 1]); // 2 vrais, 2 faux
    for (const isVrai of wanted) {
        let f, a, d;
        do {
            d = randomChoice(DEC_DENS);
            a = randInt(1, Math.min(40, d * 6));
            f = mkFrac(a, d);
        } while (!DEC_DENS.includes(f.den));
        const correct = toDecimalString(f);
        let affich = correct;
        if (isVrai === 1) {
            // faux : décaler d'un truc plausible
            const bump = randomChoice([0.1, 0.2, 0.5, 1, 0.05]);
            const v = (a / d + bump).toFixed(2);
            affich = v.replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
            if (affich === correct) affich = (a / d + 1).toString().replace('.', ',');
        }
        lignes.push(`$${renderFrac(f)} = ${affich}$`);
        bonnes.push(isVrai); // 0=Vrai, 1=Faux
    }
    const texte = `<p>Indique si chaque égalité est vraie ou fausse.</p>`;
    return makeVraiFaux(lignes, bonnes, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Valider/invalider des égalités fraction = décimal",
    });
};

/* 620-T9 (drag « a ÷ b = {{a}}/{{b}} ») SUPPRIMÉ — revue 2026-08-01 : les
 * deux bonnes étiquettes figuraient dans l'énoncé (recopie, zéro travail
 * mathématique, A2) et le geste positionnel est mieux porté par la saisie
 * en fraction verticale (arbitrage utilisateur). */

/* ──────────────────────────────────────────────────────────────
 * 620-T10 — Texte à trous saisie : a = {{num}}/b
 * ────────────────────────────────────────────────────────────── */
export const genEgaliteNumTrouSaisie = () => {
    const b = randomChoice([2, 4, 5, 10]);
    const num = randInt(2, 30);
    const aStr = toDecimalString(mkFrac(num, b));
    const phrase = `$${aStr} = \\dfrac{{{${num}}}}{${b}}$`;
    const texte = `<p>Complète l'égalité en tapant le nombre manquant.</p>`;
    return makeTexteTrou(phrase, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Compléter une égalité a = ?/b (saisie)",
        mode: 'saisie',
        strict: true,
    });
};

/* ──────────────────────────────────────────────────────────────
 * Registre des générateurs
 * ────────────────────────────────────────────────────────────── */
export const GENERATORS = {
    fraction_quotient_qcm: genFractionQuotientQcm,
    valeur_decimale_qcm: genValeurDecimaleQcm,
    fraction_quotient_texte: genFractionQuotientTexte,
    egalite_num_texte: genEgaliteNumTexte,
    egalite_trou_texte: genEgaliteTrouTexte,
    valeur_decimale_texte: genValeurDecimaleTexte,
    valeur_decimale_relier: genValeurDecimaleRelier,
    valeur_decimale_vf: genValeurDecimaleVf,
    egalite_num_trou_saisie: genEgaliteNumTrouSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
    qcm: 'QCM', drag: 'Drag', vf: 'Vrai/Faux',
    num_texte: 'Saisie (num)', trou_texte: 'Saisie (dén)',
    num_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Pas de calculatrice sur tout l'attendu (arbitrage utilisateur
    // 2026-08-01) : divisions et valeurs décimales se font de tête ou posées.
    payload.contenu = { ...payload.contenu, calculatrice: false };
    payload._meta.generatorKey = key;
    return payload;
};

export const CATEGORIES = [
    { id: 'fraction_quotient', label: 'Écrire un quotient sous forme fractionnaire', formatsList: buildFormats('fraction_quotient') },
    { id: 'valeur_decimale', label: "Valeur décimale d'une fraction", formatsList: buildFormats('valeur_decimale') },
    { id: 'egalite', label: 'Égalités à trou (num / dén manquants)', formatsList: buildFormats('egalite') },
];

const gen620 = { GENERATORS, generate, CATEGORIES };
export default gen620;
